import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const PAYMENT_LABEL = { pix: 'PIX', debit: 'Débito', credit: 'Crédito', wallet: 'Saldo' };
const PAYMENT_COLOR = {
  pix: 'bg-emerald-100 text-emerald-700',
  debit: 'bg-blue-100 text-blue-700',
  credit: 'bg-purple-100 text-purple-700',
  wallet: 'bg-orange-100 text-orange-700',
};

function fmtMoney(v) {
  return `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export default function AdminReports() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('overview');

  const load = () => {
    setLoading(true);
    api.get('/admin/reports')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const maxRevenue = useMemo(() => {
    if (!data?.dailySales) return 0;
    return Math.max(1, ...data.dailySales.map(d => d.revenue));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="screen flex items-center justify-center bg-gray-50">
        <img src="/logo.png" alt="logo" className="w-16 h-16 object-contain animate-bounce" />
      </div>
    );
  }

  const { financial, delivery, orderStatus, ratings, dailySales, topDeliverers, brandStats, topCustomers } = data;

  return (
    <div className="screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="gradient-hero px-6 pt-10 pb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-16 w-auto object-contain logo-glow" />
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => nav('/admin')} className="text-xl text-sky-200 hover:text-white">←</button>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-black">Relatórios</div>
              <div className="font-black">Contabilidade</div>
            </div>
            <button onClick={load} className="text-sky-200 hover:text-white text-sm">↺</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-100 overflow-x-auto">
        {[
          ['overview', 'Visão Geral'],
          ['financial', 'Financeiro'],
          ['daily', 'Vendas Diárias'],
          ['delivery', 'Entregas'],
          ['ranking', 'Entregadores'],
          ['brands', 'Marcas'],
          ['customers', 'Clientes'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`flex-shrink-0 px-4 py-3 text-xs font-bold whitespace-nowrap border-b-2 ${
              section === id ? 'border-primary text-primary' : 'border-transparent text-gray-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">

        {/* OVERVIEW */}
        {section === 'overview' && (
          <div className="space-y-3 fade-in">
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Receita total" value={fmtMoney(financial.totalRevenue)} color="bg-blue-50 text-blue-700" icon="💰" />
              <KPI label="Lucro líquido" value={fmtMoney(financial.netProfit)} color="bg-green-50 text-green-700" icon="📈" />
              <KPI label="Pedidos entregues" value={orderStatus.delivered} color="bg-indigo-50 text-indigo-700" icon="✅" />
              <KPI label="Taxa de conversão" value={`${orderStatus.conversionRate}%`} color="bg-purple-50 text-purple-700" icon="🎯" />
              <KPI label="Tempo médio total" value={`${delivery.avgCreateToDeliverMinutes} min`} color="bg-orange-50 text-orange-700" icon="⏱" />
              <KPI label="Avaliação média" value={ratings.averageRating ? `⭐ ${ratings.averageRating.toFixed(1)}` : '—'} color="bg-yellow-50 text-yellow-700" icon="" />
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Status dos pedidos</h3>
              <div className="space-y-2 text-sm">
                <Row label="Total criados" value={orderStatus.total} />
                <Row label="Entregues" value={orderStatus.delivered} valueClass="text-green-600 font-bold" />
                <Row label="Em andamento" value={orderStatus.inProgress} valueClass="text-orange-600 font-bold" />
                <Row label="Cancelados" value={orderStatus.cancelled} valueClass="text-red-600 font-bold" />
                <Row label="Taxa de cancelamento" value={`${orderStatus.cancellationRate}%`} valueClass="text-red-500 font-bold" />
              </div>
            </div>
          </div>
        )}

        {/* FINANCEIRO */}
        {section === 'financial' && (
          <div className="space-y-3 fade-in">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Demonstrativo</h3>
              <div className="space-y-2 text-sm">
                <Row label="(+) Receita bruta" value={fmtMoney(financial.totalRevenue)} valueClass="font-bold text-gray-800" />
                <Row label="(−) Repasse a entregadores" value={fmtMoney(-financial.totalPayouts)} valueClass="font-bold text-red-600" />
                <Row label="(=) Lucro bruto" value={fmtMoney(financial.grossProfit)} valueClass="font-black text-blue-600" bold />
                <Row label="(−) Taxas de pagamento (cartão/PIX)" value={fmtMoney(-financial.processorFees)} valueClass="font-bold text-red-500" />
                <Row label="(−) Cashback concedido" value={fmtMoney(-financial.cashbackGranted)} valueClass="font-bold text-red-500" />
                <Row label="(−) Recompensas fidelidade" value={fmtMoney(-financial.loyaltyRewards)} valueClass="font-bold text-red-500" />
                <div className="border-t pt-2 mt-2">
                  <Row label="Lucro líquido" value={fmtMoney(financial.netProfit)} valueClass="font-black text-green-600 text-lg" bold />
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-400">
                Taxa da plataforma: {(financial.platformFeeRate * 100).toFixed(1)}%
                {' • '}
                PIX: {(financial.processorFeeRates?.pix * 100 || 0).toFixed(1)}% • Cartão: {(financial.processorFeeRates?.debit * 100 || 0).toFixed(1)}%
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Receita por forma de pagamento</h3>
              <div className="space-y-3">
                {Object.entries(financial.byPaymentCount).map(([m, count]) => {
                  const rev = financial.byPaymentRevenue[m] || 0;
                  const fee = financial.processorFeesByMethod?.[m] || 0;
                  const feeRate = (financial.processorFeeRates?.[m] || 0) * 100;
                  const pct = financial.totalRevenue > 0 ? (rev / financial.totalRevenue) * 100 : 0;
                  return (
                    <div key={m}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PAYMENT_COLOR[m]}`}>{PAYMENT_LABEL[m]}</span>
                        <span className="font-bold text-gray-700">{fmtMoney(rev)} <span className="text-gray-400 font-normal">({count})</span></span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      {fee > 0 && (
                        <div className="text-[11px] text-red-500 text-right">
                          taxa {feeRate.toFixed(1)}%: −{fmtMoney(fee)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="border-t mt-3 pt-2 flex justify-between text-sm">
                <span className="text-gray-500 font-bold">Total taxas pagamento</span>
                <span className="font-black text-red-600">−{fmtMoney(financial.processorFees)}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Outros</h3>
              <div className="space-y-2 text-sm">
                <Row label="Gorjetas repassadas" value={fmtMoney(financial.totalTips)} valueClass="text-emerald-600 font-bold" />
                <Row label="Cashback usado em pedidos" value={fmtMoney(financial.cashbackUsed)} valueClass="text-blue-600 font-bold" />
              </div>
            </div>
          </div>
        )}

        {/* DAILY SALES */}
        {section === 'daily' && (
          <div className="space-y-3 fade-in">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Vendas dos últimos 30 dias</h3>
              <div className="space-y-1.5">
                {dailySales.map(d => (
                  <div key={d.date} className="flex items-center gap-2">
                    <div className="text-xs text-gray-500 w-12 flex-shrink-0">{fmtDate(d.date)}</div>
                    <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden relative">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-orange-400 flex items-center justify-end pr-2"
                        style={{ width: `${(d.revenue / maxRevenue) * 100}%` }}
                      >
                        {d.revenue > 0 && (
                          <span className="text-[10px] font-bold text-white whitespace-nowrap">
                            {fmtMoney(d.revenue)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-gray-700 w-8 text-right">{d.orders}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-gray-400 mt-3">Receita por dia | quantidade de pedidos à direita</div>
            </div>
          </div>
        )}

        {/* DELIVERY */}
        {section === 'delivery' && (
          <div className="space-y-3 fade-in">
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Tempo médio total" value={`${delivery.avgCreateToDeliverMinutes} min`} color="bg-orange-50 text-orange-700" icon="⏱" />
              <KPI label="Tempo médio entrega" value={`${delivery.avgAcceptToDeliverMinutes} min`} color="bg-blue-50 text-blue-700" icon="🛵" />
              <KPI label="Aguardando aceite" value={`${delivery.avgCreateToAcceptMinutes} min`} color="bg-purple-50 text-purple-700" icon="⏳" />
              <KPI label="Mais rápida" value={`${delivery.fastestMinutes} min`} color="bg-green-50 text-green-700" icon="⚡" />
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-700 mb-3">Detalhes</h3>
              <div className="space-y-2 text-sm">
                <Row label="Mais rápida" value={`${delivery.fastestMinutes} min`} valueClass="text-green-600 font-bold" />
                <Row label="Mais demorada" value={`${delivery.slowestMinutes} min`} valueClass="text-red-600 font-bold" />
                <Row label="Avaliação média" value={ratings.averageRating ? `${ratings.averageRating.toFixed(1)} ⭐ (${ratings.ratingsCount} avaliações)` : 'Sem avaliações'} />
              </div>
            </div>
          </div>
        )}

        {/* RANKING ENTREGADORES */}
        {section === 'ranking' && (
          <div className="space-y-2 fade-in">
            {topDeliverers.length === 0 && (
              <div className="text-center py-12 text-gray-400">Nenhum entregador cadastrado</div>
            )}
            {topDeliverers.map((d, i) => (
              <div key={d.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm ${
                  i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-700' : 'bg-gray-300'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-gray-800 text-sm truncate">{d.name}</div>
                    {d.isOnline && <span className="w-2 h-2 rounded-full bg-green-400" />}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{d.vehicle} {d.plate ? `• ${d.plate}` : ''}</div>
                  {d.avgRating && <div className="text-xs text-yellow-600">⭐ {d.avgRating.toFixed(1)} ({d.ratingsCount})</div>}
                </div>
                <div className="text-right">
                  <div className="font-black text-gray-800">{d.deliveries}</div>
                  <div className="text-[10px] text-gray-400">entregas</div>
                  <div className="text-xs text-green-600 font-bold mt-0.5">{fmtMoney(d.totalEarned)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MARCAS */}
        {section === 'brands' && (
          <div className="space-y-2 fade-in">
            {brandStats.length === 0 && (
              <div className="text-center py-12 text-gray-400">Sem dados ainda</div>
            )}
            {brandStats.map((b, i) => {
              const maxOrders = Math.max(1, ...brandStats.map(x => x.orders));
              return (
                <div key={b.brandId} className="bg-white rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">{i + 1}</div>
                      <span className="font-bold text-gray-800 text-sm">{b.brandName}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-black text-gray-800">{b.orders}</div>
                      <div className="text-xs text-green-600">{fmtMoney(b.revenue)}</div>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${(b.orders / maxOrders) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CLIENTES */}
        {section === 'customers' && (
          <div className="space-y-2 fade-in">
            {topCustomers.length === 0 && (
              <div className="text-center py-12 text-gray-400">Nenhum cliente comprou ainda</div>
            )}
            {topCustomers.map((c, i) => (
              <div key={c.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm ${
                  i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-700' : 'bg-gray-300'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-800 text-sm truncate">{c.name}</div>
                  <div className="text-xs text-gray-400">{c.orders} pedido(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-black text-green-600 text-sm">{fmtMoney(c.spent)}</div>
                  <div className="text-[10px] text-gray-400">gastos</div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      <div className="px-4 pb-4 text-[10px] text-gray-400 text-center">
        Gerado em {new Date(data.generatedAt).toLocaleString('pt-BR')}
      </div>
    </div>
  );
}

function KPI({ label, value, color, icon }) {
  return (
    <div className={`rounded-2xl p-4 ${color}`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="font-black text-xl break-words">{value}</div>
      <div className="text-xs opacity-70 mt-0.5">{label}</div>
    </div>
  );
}

function Row({ label, value, valueClass = 'text-gray-700', bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-gray-500 ${bold ? 'font-bold' : ''}`}>{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
